import { useRef, useState, type ComponentProps, type ReactNode } from "react";
import classNames from "classnames";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { isOverflowingX } from "@design-system/Tooltip/useTruncationTooltip";
import { getNodeText } from "./boardCardText";
import {
    type BoardCardTagTooltip,
    getBoardCardTagTooltip,
} from "./boardCardTagTooltip";
import styles from "./BoardCardTag.module.scss";

export type { BoardCardTagTooltip } from "./boardCardTagTooltip";

/** Marks a tag's tooltip trigger, so a control around it can style the Tag inside. */
const BOARD_CARD_TAG_TRIGGER_ATTRIBUTE = "data-board-card-tag-trigger";

const TRIGGER_PROPS = { [BOARD_CARD_TAG_TRIGGER_ATTRIBUTE]: "" };

/**
 * A board card tag's tooltip trigger, as `Tooltip`'s child: a chip button
 * around it lights the tag inside on hover. For a tag-like pill drawn
 * without `BoardCardTag`, such as a status pill.
 */
export const BoardCardTagTrigger = (props: ComponentProps<"span">) => (
    <span {...props} {...TRIGGER_PROPS} />
);

/**
 * What screen readers read before a tag's value, e.g. its field: visually
 * hidden, inside the tag or its trigger.
 */
export const BoardCardTagSpokenPrefix = ({
    children,
}: Readonly<{ children: ReactNode }>) => (
    <span className={styles.hidden}>{children} </span>
);

interface BoardCardTagProps {
    /** The field, e.g. "Due date": read before the value, and first in every tooltip. */
    field: string;
    /** The value's plain text for the tooltip. @default the text in `children` */
    value?: string;
    /** Last in the tooltip and read after the field, e.g. "3 days late". */
    detail?: string;
    /** When the tooltip shows; see `BoardCardTagTooltip`. @default "whenTruncated" */
    tooltip?: BoardCardTagTooltip;
    icon?: ReactNode;
    /**
     * The value as drawn, cut off with an ellipsis when it doesn't fit; omit
     * it for an icon-only tag. With `fieldAndValue` assistive tech reads
     * `value` instead.
     */
    children?: ReactNode;
    className?: string;
}

interface ShownTooltip {
    isOpen: boolean;
    /** Kept while closing, so the text doesn't change as it fades. */
    text?: string;
}

/**
 * A board card's tag: the 20px neutral outline `Tag`, with an optional
 * leading icon. Its tooltip, `Field · Value · Detail` or the parts that
 * aren't on show, is worked out as it opens: whether the value is cut off is
 * measured then, so renders read no layout. Works the same in `TagOverflow`'s
 * `+N` popover.
 */
export const BoardCardTag = ({
    field,
    value,
    detail,
    tooltip = "whenTruncated",
    icon,
    children,
    className,
}: Readonly<BoardCardTagProps>) => {
    const valueRef = useRef<HTMLSpanElement>(null);
    const [shown, setShown] = useState<ShownTooltip>({ isOpen: false });
    const valueText = value ?? getNodeText(children);
    const isValueDrawn = tooltip === "fieldAndValue";
    const spokenPrefix = [field, isValueDrawn ? valueText : "", detail ?? ""]
        .filter((part) => part !== "")
        .join(", ");

    const handleOpenChange = (isOpening: boolean) => {
        if (!isOpening) {
            setShown((current) => ({ ...current, isOpen: false }));
            return;
        }
        const element = valueRef.current;
        const text = getBoardCardTagTooltip({
            field,
            value: valueText,
            detail,
            tooltip,
            isTruncated: element !== null && isOverflowingX(element),
        });
        if (text !== undefined) {
            setShown({ isOpen: true, text });
        }
    };

    const tag = (
        <Tag
            size="sm"
            color="neutral"
            outline
            leftIcon={icon}
            className={classNames(className, {
                [styles.iconOnly]: children === undefined,
            })}
        >
            <BoardCardTagSpokenPrefix>{spokenPrefix}</BoardCardTagSpokenPrefix>
            {children !== undefined && (
                <span
                    ref={valueRef}
                    className={styles.value}
                    aria-hidden={isValueDrawn || undefined}
                >
                    {children}
                </span>
            )}
        </Tag>
    );
    if (tooltip === "none") {
        return tag;
    }
    return (
        <Tooltip
            content={shown.text ?? field}
            open={shown.isOpen}
            onOpenChange={handleOpenChange}
        >
            <BoardCardTagTrigger className={styles.trigger}>
                {tag}
            </BoardCardTagTrigger>
        </Tooltip>
    );
};
